import { useCallback, useEffect, useRef } from 'react';

import { RiArrowDownSLine, RiLink } from '@remixicon/react';
import { useEditorState } from '@tiptap/react';

import { useCurrentEditor } from '../Context';
import { ToolbarButton } from '../ToolbarButton';
import { useDropdownGroup } from '../useDropdownGroup';
import { LinkDialog } from './LinkDialog';
import { LINK_DIALOG_OPEN_EVENT } from './constants';

export const AddLinkButton = () => {
  const { editor } = useCurrentEditor();
  const { isOpen, open, close } = useDropdownGroup();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isActiveLink = useEditorState({
    editor,
    selector: ctx => ctx.editor?.isActive('link') ?? false,
  });

  const isActiveCode = !!useEditorState({
    editor,
    selector: ctx => ctx.editor?.isActive('code') ?? false,
  });

  const handleClick = () => {
    if (isOpen) {
      close();
      editor?.chain().focus().unsetLink().run();
    } else {
      open();
    }
  };

  const handleClose = useCallback(() => {
    close();
    if (editor) {
      const { to } = editor.state.selection;
      editor.commands.setTextSelection(to);
    }
  }, [editor, close]);

  useEffect(() => {
    const handleOpenLinkDialog = () => {
      const waitForButton = () => {
        if (
          buttonRef.current &&
          buttonRef.current.getBoundingClientRect().width > 0
        ) {
          requestAnimationFrame(() => {
            open();
          });
        } else {
          requestAnimationFrame(waitForButton);
        }
      };

      waitForButton();
    };

    window.addEventListener(LINK_DIALOG_OPEN_EVENT, handleOpenLinkDialog);

    return () => {
      window.removeEventListener(LINK_DIALOG_OPEN_EVENT, handleOpenLinkDialog);
    };
  }, [open]);

  useEffect(() => {
    if (!editor || !isOpen) return undefined;

    const initialSelection = {
      from: editor.state.selection.from,
      to: editor.state.selection.to,
    };

    const handleSelectionUpdate = () => {
      const currentSelection = editor.state.selection;

      if (
        currentSelection.from !== initialSelection.from ||
        currentSelection.to !== initialSelection.to
      ) {
        close();
      }
    };

    editor.on('selectionUpdate', handleSelectionUpdate);

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate);
    };
  }, [editor, isOpen, close]);

  const linkAttrs = editor?.getAttributes('link');
  const selection = editor?.state.selection;
  const selectedText = selection
    ? editor?.state.doc.textBetween(selection.from, selection.to, ' ')
    : '';

  return (
    <>
      <LinkDialog
        isOpen={isOpen}
        onClose={handleClose}
        anchorElement={buttonRef.current}
        initialHref={linkAttrs?.href}
        initialLinkText={selectedText}
        mode={isActiveLink ? 'edit' : 'create'}
      />
      <ToolbarButton
        ref={buttonRef}
        active={isActiveLink}
        onClick={handleClick}
        disabled={isActiveCode}
        aria-label="Add link"
        data-track-id="button.add-link"
        data-track-action={isActiveLink ? 'update' : 'create'}
      >
        <RiLink />
        <RiArrowDownSLine style={{ opacity: 0.3 }} />
      </ToolbarButton>
    </>
  );
};
